import React, { useEffect, useState } from "react";
import { Users, HeartHandshake, Megaphone, MapPin, Trees, Stethoscope } from "lucide-react";
import { CountUp } from "@/components/ui-blocks/CountUp";
import { Reveal, SectionHeader } from "@/components/ui-blocks/Reveal";
import { api, endpoints } from "@/lib/api";
import { useSite } from "@/lib/site";

const ITEMS = [
  { key: "lives_supported", label: "Lives Supported", Icon: Users, color: "navy" },
  { key: "volunteers", label: "Volunteers", Icon: HeartHandshake, color: "orange" },
  { key: "campaigns", label: "Campaigns", Icon: Megaphone, color: "green" },
  { key: "villages_reached", label: "Villages Reached", Icon: MapPin, color: "navy" },
  { key: "trees_planted", label: "Trees Planted", Icon: Trees, color: "green" },
  { key: "medical_camps", label: "Medical Camps", Icon: Stethoscope, color: "orange" },
];

const COLOR_MAP = {
  navy: "bg-[hsl(var(--surface-tint))] text-[hsl(var(--navy))]",
  orange: "bg-[hsl(var(--orange)/0.12)] text-[hsl(var(--orange))]",
  green: "bg-[hsl(var(--surface-tint-green))] text-[hsl(var(--green))]",
};

export const ImpactCounters = () => {
  const [stats, setStats] = useState(null);
  const site = useSite();
  useEffect(() => { api.get(endpoints.stats).then(setStats).catch(() => {}); }, []);
  return (
    <section id="impact" className="surface-tint">
      <div className="container-wide py-14 lg:py-20">
        <SectionHeader eyebrow="Our Impact" title="Real numbers. Real people. Real change." lead="Every figure below is tied to a program record, an audited voucher, and — above all — a person whose life is a little better today." />
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {ITEMS.map(({ key, label, Icon, color }, idx) => (
            <Reveal key={key} delay={idx * 0.05}>
              <div data-testid={`impact-counter-${key}`} className="rounded-xl border border-[hsl(var(--border))] bg-card p-5 h-full">
                <div className={`h-10 w-10 rounded-md grid place-items-center ${COLOR_MAP[color]}`}><Icon className="h-5 w-5" /></div>
                <div className="mt-4 text-3xl font-semibold text-[hsl(var(--navy))]">
                  <CountUp value={stats?.[key] || 0} />+
                </div>
                <div className="text-sm text-[hsl(var(--muted-ink))] mt-1">{label}</div>
              </div>
            </Reveal>
          ))}
        </div>

        {/* Annotated ground-reality strip (admin-editable via Site settings) */}
        <Reveal delay={0.1}>
          <div className="mt-8 relative rounded-2xl overflow-hidden border border-[hsl(var(--border))] caf-shadow" data-testid="impact-annotated-image">
            <img src={site.impact_image_url} alt="Indian school children supported by CAF programs" className="w-full h-72 md:h-80 object-cover object-top" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/25 to-black/70" />
            <div className="absolute top-0 inset-x-0 h-1 flex" aria-hidden="true">
              <div className="flex-1 bg-[hsl(var(--orange))]" /><div className="flex-1 bg-white" /><div className="flex-1 bg-[hsl(var(--green))]" />
            </div>
            {/* Left: headline */}
            <div className="absolute left-5 md:left-8 top-1/2 -translate-y-1/2 max-w-[46%] md:max-w-xs text-white">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--orange))] font-bold">Ground reality, 2026</div>
              <div className="mt-1.5 text-base md:text-2xl font-extrabold leading-snug" data-testid="impact-annotated-title">{site.impact_title}</div>
              <div className="mt-2 text-xs text-white/80 hidden md:block">{site.impact_sub}</div>
            </div>
            {/* Right: justified callout panel */}
            <div className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 flex flex-col gap-2.5 max-w-[46%] md:max-w-[300px]" data-testid="impact-callouts">
              {[
                [site.impact_callout_1, "hsl(var(--orange))"],
                [site.impact_callout_2, "#FFFFFF"],
                [site.impact_callout_3, "hsl(var(--green))"],
              ].filter(([t]) => t && t.trim()).map(([text, color], i) => (
                <div key={i} className="flex items-center gap-2.5 rounded-full bg-black/65 backdrop-blur-md border border-white/20 pl-2.5 pr-4 py-2">
                  <span className="relative flex h-2.5 w-2.5 shrink-0">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-70" style={{ background: color }}></span>
                    <span className="relative inline-flex rounded-full h-2.5 w-2.5" style={{ background: color }}></span>
                  </span>
                  <span className="text-[11px] md:text-xs font-semibold text-white leading-tight">{text}</span>
                </div>
              ))}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
